import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { AdminAudienceFilterSelection } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { useToast } from "../../components/ToastProvider";
import { datetimeLocalValueToIso, formatDateTime, isoToDatetimeLocalValue } from "../../formatting/date";
import { formatCount } from "../../formatting/number";
import { getShowOptions } from "../contacts/api";
import { ShowSelector } from "../contacts/ShowSelector";
import { getTemplateOptions } from "../templates/api";
import { buildAllowedSearchParams, confirmActiveNavigation, useUnsavedChangesBlocker } from "../../state/routeState";
import { useQuery } from "../../state/useQuery";
import { createCampaignWithoutSegment, getCampaignAudiencePreview, saveAudienceFilter } from "./api";
import { EmpresaChips, EmpresaSelector } from "./EmpresaSelector";
import { CAMPAIGN_LIST_ALLOWED_KEYS } from "./filters";
import { TemplateSelector } from "./TemplateSelector";

interface CreateForm {
  nombre: string;
  asunto: string;
  templateId: string;
  showId: string;
  // odd/tasks/campaign-audience-empresa-multiselect.md (T3): zero or more companies.
  empresas: string[];
  paisEmpresa: string;
  cargo: string;
  categoria: string;
  fechaProgramada: string;
}

const EMPTY_FORM: CreateForm = {
  nombre: "",
  asunto: "",
  templateId: "",
  showId: "",
  empresas: [],
  paisEmpresa: "",
  cargo: "",
  categoria: "",
  fechaProgramada: "",
};

type Step = 1 | 2 | 3;

const STEP_LABELS: readonly { step: Step; label: string }[] = [
  { step: 1, label: "Contenido" },
  { step: 2, label: "Audiencia" },
  { step: 3, label: "Envío" },
];

/** Order-insensitive membership comparison (mirrors `AudienceFilterEditor`'s own helper). */
function sameEmpresas(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((name, index) => name === sortedB[index]);
}

function buildCriterio(form: CreateForm): AdminAudienceFilterSelection {
  return {
    showId: form.showId === "" ? null : form.showId,
    paisEmpresa: form.paisEmpresa.trim() === "" ? null : form.paisEmpresa.trim(),
    empresa: null,
    empresas: form.empresas.length === 0 ? null : form.empresas,
    cargo: form.cargo.trim() === "" ? null : form.cargo.trim(),
    categoria: form.categoria.trim() === "" ? null : form.categoria.trim(),
  };
}

/** Local debounce helper, same convention as `EmpresaSelector`'s own `useDebouncedValue`. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);
  return debounced;
}

const AUDIENCE_DEBOUNCE_MS = 400;

function audienceCountText(total: number | undefined, settling: boolean): string {
  if (settling || total === undefined) return "Calculando…";
  return total === 1 ? "≈ 1 contacto" : `≈ ${formatCount(total)} contactos`;
}

/**
 * REDISENO-PANEL.md §5.2 — "Nueva campaña", a 3-step modal (Contenido →
 * Audiencia → Envío) replacing the former single-step "Nueva campaña /
 * Crear lote" panel. §1.1: batch creation is gone entirely — this always
 * creates ONE `borrador` campaign via `createCampaignWithoutSegment`, then
 * `saveAudienceFilter` when any audience criterion was set. On success it
 * closes, navigates to `/campanas/:id`, and shows a toast — it no longer
 * shows its own inline "campaign created" result screen (`scheduleCampaignBatch`
 * and the batch request/response types stay in `./api.ts`/`api/types.ts`,
 * just unused by this panel now).
 */
export function CampaignCreatePanel({
  onClose,
  onCreated,
  onDirtyChange,
  onSubmittingChange,
  restoreFocusFallback,
}: {
  onClose: () => void;
  onCreated: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubmittingChange?: (submitting: boolean) => void;
  /**
   * Codex finding (round 4, PR #381): this panel can be opened from a
   * shell-level trigger (`AppShell.tsx`'s desktop-only "+ Nueva campaña")
   * that may no longer be visible by the time the panel closes — e.g. the
   * operator resized/rotated below 768px first, hiding the desktop rail.
   * `Modal`'s default focus restoration would then try to focus a hidden
   * element and silently fall back to `<body>`. Passed straight through to
   * both `Modal`s this panel renders; called lazily at CLOSE time so the
   * caller can decide (based on whatever is true THEN, not when the panel
   * opened) whether the original trigger is still usable — return `null` to
   * keep `Modal`'s normal restoration.
   */
  restoreFocusFallback?: () => HTMLElement | null;
}) {
  const navigate = useNavigate();
  // Codex findings review (panel-redesign-v3-04, #5): this panel mounts
  // directly on whichever route opened it (`/campanas` from CampaignListScreen,
  // the global shell trigger, or Overview) -- `useSearchParams()` here reads
  // that SAME host route's current query string, same as
  // `CampaignDetailScreen`'s own "‹ Campañas" back link already does for the
  // allow-listed `estado`/`page`/`size` keys. Elsewhere (no such keys in the
  // URL) this is simply empty, so it never adds anything out of place.
  //
  // native review finding R3-001 (panel-redesign-v3-04): that "empty
  // elsewhere" assumption does not hold for a host route that happens to use
  // the SAME generic `page`/`size` keys for something else entirely (e.g. a
  // paginated contacts list) -- those would also pass the allow-list and get
  // carried onto `/campanas/:id`, even though they were never the campaign
  // list's own state. `estado`/`page`/`size` only mean "the campaign list's
  // filters" when this panel is actually mounted ON the campaign list route
  // itself; everywhere else the suffix must stay empty regardless of what
  // the host URL's query string contains.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const listContextSuffix = useMemo(() => {
    // bounded-writer fix (panel-redesign-v3-04, item a): accept one trailing
    // slash ("/campanas/") as the same list route -- React Router's own
    // matching already treats it as equivalent, so an exact `!==` comparison
    // here silently dropped the list's estado/page/size context whenever the
    // host happened to keep that trailing slash on the URL.
    if (location.pathname !== "/campanas" && location.pathname !== "/campanas/") {
      return "";
    }
    const allowed = buildAllowedSearchParams(searchParams.toString(), CAMPAIGN_LIST_ALLOWED_KEYS);
    const asString = allowed.toString();
    return asString ? `?${asString}` : "";
  }, [location.pathname, searchParams]);
  const { showToast } = useToast();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // odd/tasks/empresa-options-search-by-show.md (T4): `EmpresaSelector`'s own
  // Show-change existence check can itself be pending or fail -- this must
  // hard-block the final submit the same way `submitting` does.
  const [empresaValidation, setEmpresaValidation] = useState<"valid" | "pending" | "error">("valid");
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const requestCloseRef = useRef<() => void>(() => {});
  // native review finding (CRITICAL, panel-redesign-v3-04 T1): a successful
  // create must navigate with `useUnsavedChangesBlocker`'s guard GENUINELY
  // disarmed -- not merely "probably fine because the host happens to unmount
  // this panel in the same tick as `navigate()`". The typed fields are never
  // cleared on success, so `dirty` alone would stay `true` forever; a prior
  // attempt that only added a `completed` boolean right before calling
  // `navigate()` directly was REJECTED by native review -- React 18 batches
  // state updates together, so `navigate()` still ran while the SAME render
  // that set `completed` hadn't committed yet, and a guard consulted at that
  // exact instant (e.g. `confirmActiveNavigation()` from an AppShell nav-link
  // click, or the browser Back button) would still see the pre-success
  // `dirty`/`submitting` values. Storing the created id and performing the
  // actual navigation from a `useEffect` below instead guarantees the guard
  // has already re-evaluated to `false` in a COMMITTED render (this hook's own
  // effect — declared earlier, so it runs first in commit order — has already
  // removed/updated its `guardStack` entry) before `navigate()` ever runs.
  const [completedCampaignId, setCompletedCampaignId] = useState<string | null>(null);

  const dirty =
    completedCampaignId === null &&
    (form.nombre !== EMPTY_FORM.nombre ||
      form.asunto !== EMPTY_FORM.asunto ||
      form.templateId !== EMPTY_FORM.templateId ||
      form.showId !== EMPTY_FORM.showId ||
      form.paisEmpresa !== EMPTY_FORM.paisEmpresa ||
      !sameEmpresas(form.empresas, EMPTY_FORM.empresas) ||
      form.cargo !== EMPTY_FORM.cargo ||
      form.categoria !== EMPTY_FORM.categoria ||
      form.fechaProgramada !== EMPTY_FORM.fechaProgramada);

  // `dirty` (edición sin guardar) es un bloqueo SUAVE -- confirma antes de salir. `submitting`
  // (POST en vuelo) es el único bloqueo DURO.
  useUnsavedChangesBlocker(submitting || dirty, submitting);

  // Codex findings review (panel-redesign-v3-04, #5): read via a ref, same
  // convention as `onCreatedRef` right below -- the effect that consumes it
  // only fires once (`completedCampaignId` goes from `null` to set), but a
  // ref keeps it out of that effect's own dependency array without going
  // stale between this render and the one that runs it.
  const listContextSuffixRef = useRef(listContextSuffix);
  listContextSuffixRef.current = listContextSuffix;

  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;
  useEffect(() => {
    if (completedCampaignId === null) {
      return;
    }
    // By the time this effect runs, the render that set `completedCampaignId`
    // has already committed with `dirty`/`submitting` both `false` (see the
    // comment above `completedCampaignId`'s declaration) -- `navigate()` is
    // safe here even if this panel is still mounted when it runs, as far as
    // THIS panel's own guard goes.
    //
    // Codex findings review (panel-redesign-v3-04, #1): this shell-level
    // modal can be opened OVER an already-dirty screen (e.g.
    // SegmentDetailScreen/TemplateReplaceEditor mid-edit) -- navigating away
    // unconditionally would silently discard THAT screen's unsaved edits
    // with no confirm at all, even though `confirmActiveNavigation()` (the
    // same mechanism AppShell's nav-links/Back button already use) exists
    // precisely to ask every other currently-armed guard first. The
    // campaign is already created server-side either way -- only the
    // NAVIGATION is held back on a decline, never the create outcome itself
    // (the modal still closes/reports completion via `onCreated`).
    if (confirmActiveNavigation()) {
      navigate(`/campanas/${completedCampaignId}${listContextSuffixRef.current}`);
    }
    onCreatedRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCampaignId, navigate]);

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  useEffect(() => {
    return () => onDirtyChangeRef.current?.(false);
  }, []);

  const onSubmittingChangeRef = useRef(onSubmittingChange);
  onSubmittingChangeRef.current = onSubmittingChange;
  useEffect(() => {
    onSubmittingChangeRef.current?.(submitting);
  }, [submitting]);
  useEffect(() => {
    return () => onSubmittingChangeRef.current?.(false);
  }, []);

  const { data: templateOptions } = useQuery({ key: ["template-options"], queryFn: () => getTemplateOptions() });
  const selectedTemplate = templateOptions?.find((option) => option.templateId === form.templateId);

  const { data: showOptions } = useQuery({ key: ["show-options"], queryFn: () => getShowOptions() });
  const selectedShowName = showOptions?.find((option) => option.showId === form.showId)?.nombre;

  // §5.2: "con debounce 400 ms en los campos de texto" — Show/Empresas apply
  // live (a <select>/chip toggle, not free text), same convention
  // `AudienceFilterEditor`/this panel's own old `buildCriterio` comment
  // already established.
  //
  // Codex findings review (panel-redesign-v3-04, #10): trimmed here, the
  // same normalization `buildCriterio` already applies for the real submit
  // -- otherwise incidental leading/trailing whitespace reached
  // `getCampaignAudiencePreview` verbatim while the actual saved filter never
  // would have, which could show a preview count for a slightly different
  // (untrimmed) criterion than the one that ends up saved.
  const debouncedPais = useDebouncedValue(form.paisEmpresa, AUDIENCE_DEBOUNCE_MS).trim();
  const debouncedCargo = useDebouncedValue(form.cargo, AUDIENCE_DEBOUNCE_MS).trim();
  const debouncedCategoria = useDebouncedValue(form.categoria, AUDIENCE_DEBOUNCE_MS).trim();
  const empresasIdentity = form.empresas.join("\u0000");

  // Codex findings review (panel-redesign-v3-04, #1): País/Cargo/Categoría
  // are debounced -- a quick Siguiente → "Crear borrador" within the 400ms
  // window could submit (and the Resumen could show) a preview count
  // computed from the PREVIOUS criteria, not the ones just typed. "Settled"
  // means the debounced (trimmed) value actually sent to the query already
  // matches the live (trimmed) field value -- i.e. no pending debounce tick
  // is still in flight for any of the three text criteria.
  const audienceDebouncePending =
    form.paisEmpresa.trim() !== debouncedPais ||
    form.cargo.trim() !== debouncedCargo ||
    form.categoria.trim() !== debouncedCategoria;

  const {
    data: audiencePreview,
    error: audiencePreviewError,
    loading: audiencePreviewLoading,
    refreshing: audiencePreviewRefreshing,
    refetch: refetchAudiencePreview,
  } = useQuery({
    key: [
      "campaign-audience-preview",
      form.showId || null,
      debouncedPais || null,
      empresasIdentity || null,
      debouncedCargo || null,
      debouncedCategoria || null,
    ],
    queryFn: () =>
      getCampaignAudiencePreview({
        showId: form.showId || undefined,
        paisEmpresa: debouncedPais || undefined,
        empresas: form.empresas.length > 0 ? form.empresas : undefined,
        cargo: debouncedCargo || undefined,
        categoria: debouncedCategoria || undefined,
        page: 1,
        size: 1,
      }),
    // Codex findings review (#2): clicking Siguiente WITHIN the 400ms
    // debounce window used to disable this query entirely (`step === 2`
    // only) before the latest keystroke's criteria ever got fetched --
    // step 3's Resumen then showed a stale count forever (disabling a query
    // freezes its last result, it does not clear it). Keeping it enabled on
    // step 3 too lets the still-running debounce converge on the live
    // criteria a moment after arriving there.
    enabled: step === 2 || step === 3,
  });
  // Codex findings review (#3): an errored preview query left `total`
  // `undefined` forever (useQuery never fabricates data on failure), which
  // `audienceCountText` renders as "Calculando…" indefinitely -- indistinguishable
  // from a slow-but-working request. Only genuinely true when there is no
  // data to show AND a request has already failed (see `stale`'s own
  // contract in useQuery.ts): a background refresh failing while a prior
  // count is still on screen keeps showing that last known count instead.
  const audiencePreviewFailed = audiencePreview === undefined && audiencePreviewError !== undefined;

  // Codex findings review (#1): a debounce tick still pending, or the query
  // itself still fetching for the already-settled criteria, both mean
  // whatever `audiencePreview.total` currently holds does NOT yet reflect
  // the live criteria. `audiencePreviewFailed` (data undefined + errored) is
  // its own case with its own retry panel -- `refreshing` requires existing
  // data, so it can never overlap with it.
  const audiencePreviewSettling = audienceDebouncePending || audiencePreviewLoading || audiencePreviewRefreshing;

  // odd/tasks/empresa-options-search-by-show.md (T4) + Codex findings review
  // (#1): the final submit is hard-blocked while a POST is in flight, the
  // Empresa/Show existence check hasn't resolved, OR the audience preview
  // hasn't settled on the live (just-typed) criteria yet.
  const submitBlocked = submitting || empresaValidation !== "valid" || audiencePreviewSettling;

  // Codex findings review: this failure panel (message + onRetry) was
  // duplicated verbatim in step 2's audience box and step 3's summary — one
  // definition keeps their wording and retry wiring in sync.
  const audiencePreviewFailurePanel = (
    <FeedbackPanel variant="error" message="No se pudo calcular la audiencia." onRetry={refetchAudiencePreview} />
  );

  function requestClose() {
    if (submitting) {
      return;
    }
    if (dirty) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  }
  requestCloseRef.current = requestClose;

  function finishCreate(campaignId: string, filterFailed: boolean) {
    setSubmitting(false);
    // Codex findings review (panel-redesign-v3-04, #11): the campaign WAS
    // created -- a plain auto-dismissing success toast is the wrong shape
    // when the audience filter silently failed to save alongside it. Warning
    // + persistent (no auto-dismiss) so the operator actually notices before
    // it disappears, same as `ConfirmDialog`'s own "never silently discard a
    // partial failure" convention elsewhere in this panel.
    if (filterFailed) {
      showToast(
        "Campaña creada como borrador. El criterio de audiencia no se pudo guardar automáticamente; ajústalo desde el detalle.",
        { variant: "warning", persistent: true },
      );
    } else {
      showToast("Borrador creado. Revísalo y prográmalo cuando esté listo.");
    }
    // Navigation itself happens in the `completedCampaignId` effect above,
    // once this render (dirty=false, submitting=false) has committed.
    setCompletedCampaignId(campaignId);
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitBlocked) return;

    setSubmitting(true);
    setSubmitError(null);

    createCampaignWithoutSegment({
      nombre: form.nombre,
      asunto: form.asunto.trim() === "" ? null : form.asunto,
      templateId: form.templateId,
      fechaProgramada: form.fechaProgramada,
    }).then(
      ({ campaignId }) => {
        const criterio = buildCriterio(form);
        const hasCriterio = Object.values(criterio).some((value) => value !== null);
        if (!hasCriterio) {
          finishCreate(campaignId, false);
          return;
        }
        // Si `saveAudienceFilter` falla DESPUÉS de que la campaña ya se creó,
        // la campaña YA EXISTE en el servidor -- nunca tratarlo como una
        // creación fallida; §5.2: igual se navega, con el aviso vía toast.
        saveAudienceFilter(campaignId, criterio).then(
          () => finishCreate(campaignId, false),
          () => finishCreate(campaignId, true),
        );
      },
      (error: unknown) => {
        setSubmitting(false);
        setSubmitError(error instanceof Error ? error.message : "No se pudo crear la campaña.");
        // `form` queda intacto -- contrato de preservación en error.
      },
    );
  }

  const canAdvanceFromStep1 = form.nombre.trim() !== "" && form.templateId !== "";
  const audienceSummary = `${selectedShowName ?? "Todos los shows"}${
    form.empresas.length > 0 ? ` · ${form.empresas.length} empresas` : ""
  } · ${audienceCountText(audiencePreview?.total, audiencePreviewSettling)}`;

  return (
    <Modal
      ariaLabel="Nueva campaña"
      onClose={() => requestCloseRef.current()}
      restoreFocusFallback={restoreFocusFallback}
      suspendFocusTrap={confirmingDiscard}
    >
      <div className="v2-modal-v2 v2-modal-v2--w680 v2-campaign-create-panel">
        <header className="v2-modal-v2__header v2-modal-v2__header--stacked">
          <div className="v2-modal-v2__header-row">
            <h2>Nueva campaña</h2>
            <ModalCloseButton onClose={requestClose} disabled={submitting} />
          </div>
          <ol className="v2-campaign-create__steps" aria-label="Pasos">
            {STEP_LABELS.map(({ step: stepNumber, label }) => (
              <li
                key={stepNumber}
                aria-current={step === stepNumber ? "step" : undefined}
                className={
                  step > stepNumber
                    ? "v2-campaign-create__step v2-campaign-create__step--done"
                    : step === stepNumber
                      ? "v2-campaign-create__step v2-campaign-create__step--active"
                      : "v2-campaign-create__step"
                }
              >
                <span className="v2-campaign-create__step-bar" aria-hidden="true" />
                <span className="v2-campaign-create__step-label">
                  {stepNumber}. {label}
                </span>
              </li>
            ))}
          </ol>
        </header>
        <div className="v2-modal-v2__body">
          {confirmingDiscard && (
            <ConfirmDialog
              message="¿Descartar la campaña? Tienes cambios sin guardar. Si sales ahora se pierden."
              confirmLabel="Descartar"
              cancelLabel="Seguir editando"
              onConfirm={() => {
                setConfirmingDiscard(false);
                onClose();
              }}
              onCancel={() => setConfirmingDiscard(false)}
            />
          )}

          <form id="campaign-create-form" className="v2-campaign-create-panel__form" onSubmit={handleSubmit}>
            {step === 1 && (
              <fieldset className="v2-modal-v2__group">
                <legend>Contenido</legend>
                <Field label="Nombre de la campaña" htmlFor="campaign-create-nombre">
                  <input
                    id="campaign-create-nombre"
                    value={form.nombre}
                    onChange={(event) => setForm({ ...form, nombre: event.target.value })}
                    placeholder="Ej. Invitación AUSA 2026"
                    required
                    disabled={submitting}
                  />
                </Field>
                <Field label="Asunto del correo" htmlFor="campaign-create-asunto">
                  <input
                    id="campaign-create-asunto"
                    value={form.asunto}
                    onChange={(event) => setForm({ ...form, asunto: event.target.value })}
                    placeholder="Lo que verá el contacto en su bandeja"
                    maxLength={255}
                    disabled={submitting}
                  />
                  <span className="v2-modal-v2__hint">Opcional. Máximo 255 caracteres.</span>
                </Field>
                <TemplateSelector
                  id="campaign-create-template"
                  value={form.templateId}
                  onChange={(templateId) => setForm({ ...form, templateId })}
                  disabled={submitting}
                  activeOnly
                />
                <span className="v2-modal-v2__hint">Solo se muestran plantillas activas.</span>
              </fieldset>
            )}

            {/* Always mounted (never conditionally unmounted like steps 1/3):
                `EmpresaSelector`'s Show-change validation check
                (`onValidationChange`) resets to "valid" on unmount — if this
                fieldset unmounted when stepping away from Audiencia, a
                still-pending/failed check would silently stop blocking the
                final submit at step 3. Visibility alone (`display: none`)
                keeps that effect's lifecycle — and the operator's typed
                Show/Empresa/país/cargo/categoría — intact across steps. */}
            <fieldset className="v2-modal-v2__group" style={{ display: step === 2 ? undefined : "none" }}>
                <legend>Audiencia</legend>
                <p className="v2-modal-v2__hint v2-modal-v2__hint--lead">
                  Elige a quién enviar. Si no seleccionas nada, la campaña va a todos los contactos activos.
                </p>
                <div className="v2-audience-grid">
                  <ShowSelector
                    id="campaign-create-show"
                    value={form.showId}
                    onChange={(showId) => setForm({ ...form, showId })}
                    disabled={submitting}
                    allowEmpty
                  />
                  <EmpresaSelector
                    id="campaign-create-empresa"
                    value={form.empresas}
                    onChange={(empresas) => setForm({ ...form, empresas })}
                    disabled={submitting}
                    showId={form.showId || undefined}
                    onValidationChange={setEmpresaValidation}
                    hideChips
                  />
                  <Field label="País de la empresa" htmlFor="campaign-create-pais">
                    <input
                      id="campaign-create-pais"
                      value={form.paisEmpresa}
                      onChange={(event) => setForm({ ...form, paisEmpresa: event.target.value })}
                      disabled={submitting}
                    />
                  </Field>
                  <Field label="Cargo" htmlFor="campaign-create-cargo">
                    <input
                      id="campaign-create-cargo"
                      value={form.cargo}
                      onChange={(event) => setForm({ ...form, cargo: event.target.value })}
                      disabled={submitting}
                    />
                  </Field>
                  <Field label="Categoría" htmlFor="campaign-create-categoria">
                    <input
                      id="campaign-create-categoria"
                      value={form.categoria}
                      onChange={(event) => setForm({ ...form, categoria: event.target.value })}
                      disabled={submitting}
                    />
                  </Field>
                </div>
                <EmpresaChips
                  value={form.empresas}
                  onChange={(empresas) => setForm({ ...form, empresas })}
                  disabled={submitting}
                />
                <div className="v2-campaign-create__audience-box" aria-live="polite">
                  {audiencePreviewFailed ? (
                    audiencePreviewFailurePanel
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M8.5 8a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0-7 0M4.5 20c.7-3.8 4-6 7.5-6s6.8 2.2 7.5 6" />
                      </svg>
                      <span className="v2-campaign-create__audience-text">
                        <strong>{audienceCountText(audiencePreview?.total, audiencePreviewSettling)}</strong>
                        <span>Contactos activos que cumplen los criterios</span>
                      </span>
                    </>
                  )}
                </div>
            </fieldset>

            {step === 3 && (
              <fieldset className="v2-modal-v2__group">
                <legend>Envío</legend>
                {/* Codex findings review (#4): the Audiencia fieldset above
                    stays mounted-but-hidden (display:none) at step 3 so
                    EmpresaSelector's own validation effect survives the step
                    change -- but that also hides ITS pending/error
                    FeedbackPanel from view, leaving "Crear borrador" disabled
                    with no visible reason here. Surface the same reason at
                    step 3, with a way back to actually resolve it. */}
                {empresaValidation !== "valid" && (
                  <>
                    <FeedbackPanel
                      variant={empresaValidation === "error" ? "error" : "stale"}
                      message={
                        empresaValidation === "error"
                          ? "No se pudo confirmar las empresas seleccionadas para el show elegido."
                          : "Verificando las empresas seleccionadas para el show elegido…"
                      }
                    />
                    <button
                      type="button"
                      className="v2-u-target-min v2-btn-secondary"
                      onClick={() => setStep(2)}
                    >
                      Volver a Audiencia
                    </button>
                  </>
                )}
                <Field label="Fecha y hora de envío" htmlFor="campaign-create-fecha" className="v2-campaign-create__fecha">
                  <input
                    id="campaign-create-fecha"
                    type="datetime-local"
                    value={isoToDatetimeLocalValue(form.fechaProgramada)}
                    onChange={(event) =>
                      setForm({ ...form, fechaProgramada: datetimeLocalValueToIso(event.target.value) })
                    }
                    required
                    disabled={submitting}
                  />
                  <span className="v2-modal-v2__hint">
                    La campaña queda como borrador; la programas cuando la revises.
                  </span>
                </Field>
                <div className="v2-campaign-create__summary">
                  <h3>Resumen</h3>
                  <dl>
                    <div>
                      <dt>Nombre</dt>
                      <dd>{form.nombre || "—"}</dd>
                    </div>
                    <div>
                      <dt>Asunto</dt>
                      {/* Codex finding (PR #383 round 5): a plain truthiness
                          check rendered a visually blank value for a
                          whitespace-only Asunto, while `handleSubmit`
                          normalizes the SAME input to `null` via
                          `form.asunto.trim() === ""` — apply the same
                          trim-based check here so the review shows what will
                          actually be saved. */}
                      <dd>{form.asunto.trim() === "" ? "Sin asunto" : form.asunto}</dd>
                    </div>
                    <div>
                      <dt>Plantilla</dt>
                      <dd>
                        {selectedTemplate
                          ? `${selectedTemplate.nombre} — ${selectedTemplate.producto} · v${selectedTemplate.version}`
                          : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt>Audiencia</dt>
                      <dd>{audiencePreviewFailed ? audiencePreviewFailurePanel : audienceSummary}</dd>
                    </div>
                    <div>
                      <dt>Envío</dt>
                      <dd>{form.fechaProgramada ? formatDateTime(form.fechaProgramada) : "Elige una fecha"}</dd>
                    </div>
                  </dl>
                </div>
              </fieldset>
            )}

            {submitError && <p role="alert">{submitError}</p>}
          </form>
        </div>
        <footer className="v2-modal-v2__footer v2-modal-v2__footer--plain">
          <span className="v2-modal-v2__footer-note">Paso {step} de 3</span>
          <button
            type="button"
            className="v2-u-target-min v2-btn-secondary"
            onClick={step === 1 ? requestClose : () => setStep((current) => (current - 1) as Step)}
            disabled={submitting}
          >
            {step === 1 ? "Cancelar" : "Atrás"}
          </button>
          {step < 3 ? (
            <button
              type="button"
              className="v2-u-target-min v2-btn-primary"
              onClick={() => setStep((current) => (current + 1) as Step)}
              disabled={step === 1 && !canAdvanceFromStep1}
            >
              Siguiente
            </button>
          ) : (
            <button
              type="submit"
              form="campaign-create-form"
              className="v2-u-target-min v2-btn-primary"
              disabled={submitBlocked}
              aria-busy={submitting}
            >
              {submitting ? "Creando…" : "Crear borrador"}
            </button>
          )}
        </footer>
      </div>
    </Modal>
  );
}
